import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus, 
  BarChart3,
  X,
  ArrowLeft,
  Pin
} from 'lucide-react';
import MultiSelect from '@/components/ui/multi-select';
import Button from '@/components/Button';
import WatchlistCard from '@/components/WatchlistCard';
import WatchlistDetailModal from '@/components/WatchlistDetailModal';
import SimpleMultiSelect from '@/components/ui/SimpleMultiSelect';
import { API_BASE_URL } from '../config';
import { formatMetricLabel } from '@/lib/utils';



const WatchlistPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedWatchlistId, setSelectedWatchlistId] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'dynamic',
    description: '',
    updateSchedule: 'auto',
    updateTime: '9:00 AM',
    criteria: [{ key: '', op: '', value: '' }],
    staticStocks: [],
    alertsEnabled: true
  });

  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [metricMeta, setMetricMeta] = useState({});

  const fetchAvailableMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/screener/symbol-group/filtering-metrics/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      setAvailableMetrics(result.data || []);
    } catch (err) {
      console.error('Failed to fetch available metrics:', err);
    }
  };

  const fetchMetricDetails = async (metric) => {
    if (!metric || metricMeta[metric]) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/screener/symbol-group/filtering-metrics/?metric=${metric}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      setMetricMeta(prev => ({ ...prev, [metric]: result }));
    } catch (err) {
      console.error(`Failed to fetch details for metric ${metric}:`, err);
    }
  };

  // Helper: convert ISO date to "x days/hours ago"
  const timeAgo = (iso) => {
    if (!iso) return 'Unknown';
    try {
      const then = new Date(iso);
      const now = new Date();
      const diffMs = now - then;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHour / 24);
      if (diffDay > 0) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
      if (diffHour > 0) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
      if (diffMin > 0) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
      return 'Just now';
    } catch (e) {
      return 'Unknown';
    }
  };

  // Fetch watchlists from API (reusable)
  const fetchWatchlists = async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No access token found. Please sign in.');
        setLoading(false);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/screener/symbol-groups/`, {
        signal,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!res.ok) {
        const text = await res.text().catch(() => null);
        const message = text || `HTTP ${res.status}`;
        setError(message);
        setLoading(false);
        return;
      }
      const data = await res.json();
      const mapped = data.map((wl) => ({
        id: wl.id,
        name: wl.group_name,
        type: wl.is_dynamic ? 'dynamic' : 'static',
        stockCount: Array.isArray(wl.symbols) ? wl.symbols.length : 0,
        icon: wl.is_dynamic ? BarChart3 : Pin,
        topHoldings: [], // Backend doesn't provide symbols data here, just IDs
        lastUpdate: timeAgo(wl.updated_at || wl.created_at),
        alerts: 0,
        raw: wl
      }));
      setWatchlists(mapped);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Failed to fetch watchlists', err);
        setError(err.message || 'Failed to load watchlists');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch watchlists on mount
  useEffect(() => {
    const controller = new AbortController();
    fetchWatchlists(controller.signal);
    fetchAvailableMetrics();
    return () => controller.abort();
  }, []);

  // Create a new watchlist by POSTing to the API
  const handleCreateWatchlist = async () => {
    setCreateLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No access token found. Please sign in.');
        setCreateLoading(false);
        return;
      }

      // Build payload according to NEW backend API shape.
      const payload = {
        group_name: formData.name,
        is_dynamic: formData.type === 'dynamic',
        auto_update: true,
        notify_on_change: true,
        analyst_prediction_alert: formData.alertsEnabled
      };
      
      if (formData.type === 'static') {
        payload.symbols = (formData.staticStocks || []).map(s => {
          if (s && typeof s === 'object' && s.id) return s.id;
          if (typeof s === 'number') return s;
          if (typeof s === 'string') {
            const asNum = parseInt(s, 10);
            return Number.isNaN(asNum) ? null : asNum; // Fallback to null if not a number
          }
          return null;
        }).filter(id => id !== null);
      } else {
        payload.criteria = formData.criteria || [];
      }

      const res = await fetch(`${API_BASE_URL}/api/screener/symbol-groups/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        setModalError('Please fill all fields properly and try again.');
        setCreateLoading(false);
        return;
      }

      // created - refresh list
      await fetchWatchlists();
      // close modal and reset form
      setIsModalOpen(false);
      setModalError(null);
      setFormData({
        name: '',
        type: 'dynamic',
        description: '',
        updateSchedule: 'auto',
        updateTime: '9:00 AM',
        criteria: [{ key: '', op: '', value: '' }],
        staticStocks: [],
        alertsEnabled: true
      });
    } catch (err) {
      console.error('Failed to create watchlist', err);
      setModalError('Please fill all fields properly and try again.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleViewWatchlist = (id) => {
    setSelectedWatchlistId(id);
    setIsDetailModalOpen(true);
  };

  const handleDeleteWatchlist = async (id) => {
    if (!window.confirm('Are you sure you want to delete this watchlist?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/screener/symbol-groups/${id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to delete');
      await fetchWatchlists();
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  // Filter watchlists based on active filter and search query
  const filteredWatchlists = watchlists.filter(watchlist => {
    if (activeFilter !== 'all' && watchlist.type !== activeFilter) return false;
    return !searchQuery || watchlist.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Available operators mapped to backend values
  const operators = [
    { value: 'gt', label: '>' },
    { value: 'lt', label: '<' },
    { value: 'gte', label: '>=' },
    { value: 'lte', label: '<=' },
    { value: 'equals', label: '=' },
    { value: 'not_equals', label: '!=' }
  ];

  // Form hepers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleConditionChange = (index, field, value) => {
    const newCriteria = [...formData.criteria];
    newCriteria[index] = { ...newCriteria[index], [field]: value };
    
    if (field === 'key') {
      newCriteria[index].value = ''; // Reset value when key changes
      fetchMetricDetails(value);
    }
    
    handleInputChange('criteria', newCriteria);
  };

  const addCondition = () => {
    handleInputChange('criteria', [
      ...formData.criteria,
      { key: '', op: '', value: '' },
    ]);
  };

  const removeCondition = (index) => {
    const newCriteria = formData.criteria.filter((_, i) => i !== index);
    handleInputChange('criteria', newCriteria);
  };

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* Header Section */}
      <div className="bg-gray-800 text-white px-6 py-4">
        <p className="text-sm text-gray-200">
          Your curated list of assets, automatically updated and screened
        </p>
      </div>

      {/* Navigation and Actions Bar */}
      <div className="bg-gray-100 border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Filter Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveFilter('dynamic')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === 'dynamic'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Dynamic
            </button>
            <button
              onClick={() => setActiveFilter('static')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === 'static'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Static
            </button>
          </div>

          {/* Search and New Watchlist Button */}
          <div className="flex items-center gap-3 sm:flex-1 sm:justify-end">
            {/* Search Bar */}
            <div className="relative flex-1 sm:flex-none sm:w-64">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* New Watchlist Button */}
            <Button
              icon={Plus}
              label={"New Watchlist"}
              onClick={() => {
                setIsModalOpen(true);
                setStep(1);
                setModalError(null);
              }}
            />
          </div>
        </div>
      </div>

      {/* Watchlist Cards Section */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWatchlists.map((watchlist) => (
            <WatchlistCard 
              key={watchlist.id} 
              watchlist={watchlist} 
              onView={handleViewWatchlist}
              onDelete={handleDeleteWatchlist}
            />
          ))}
        </div>

        {/* Empty State */}
        {filteredWatchlists.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-gray-500 text-lg mb-2">No watchlists found</p>
            <p className="text-gray-400 text-sm">
              {activeFilter !== 'all' 
                ? `Try adjusting your filter or create a new ${activeFilter} watchlist`
                : 'Create your first watchlist to get started'}
            </p>
          </div>
        )}

        {/* Loading and Error States */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500 text-lg">Loading watchlists...</p>
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center py-12">
            <p className="text-red-500 text-lg">{error}</p>
          </div>
        )}
      </div>

      {/* Create New Watchlist Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white sm:rounded-lg shadow-xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-t-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-900">
                Create New Watchlist
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {step === 1 ? (
                /* Step 1: Choose Watchlist Type */
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Choose Watchlist Type
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Dynamic Watchlist Card */}
                    <div
                      onClick={() => handleInputChange('type', 'dynamic')}
                      className={`cursor-pointer p-6 rounded-lg border-2 transition-all h-full ${
                        formData.type === 'dynamic'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="mb-4">
                        <div className="w-10 h-10 bg-white rounded flex items-center justify-center border border-gray-100 shadow-sm">
                           {/* Using BarChart3 as a placeholder for the colorful icon */}
                           <BarChart3 className={formData.type === 'dynamic' ? "text-blue-600" : "text-gray-500"} size={24} />
                        </div>
                      </div>
                      <h4 className="text-base font-semibold text-gray-900 mb-2">Dynamic Watchlist</h4>
                      <p className="text-sm text-gray-500 leading-relaxed">
                        Auto-updates based on screening criteria. Perfect for tracking stocks that meet specific metrics.
                      </p>
                    </div>

                    {/* Static Watchlist Card */}
                    <div
                      onClick={() => handleInputChange('type', 'static')}
                      className={`cursor-pointer p-6 rounded-lg border-2 transition-all h-full ${
                        formData.type === 'static'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="mb-4">
                        <div className="w-10 h-10 bg-white rounded flex items-center justify-center border border-gray-100 shadow-sm">
                          <Pin className={formData.type === 'static' ? "text-red-500" : "text-gray-500"} size={24} />
                        </div>
                      </div>
                      <h4 className="text-base font-semibold text-gray-900 mb-2">Static Watchlist</h4>
                      <p className="text-sm text-gray-500 leading-relaxed">
                        Manually curated list of stocks. Add or remove stocks as needed.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Step 2: Form Details */
                <div className="space-y-6">
                  {/* Watchlist Details Section */}
                  <div className="space-y-4">
                    {/* Watchlist Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Watchlist Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="e.g., Tech Growth Stocks"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description (Optional)
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Describe your watchlist..."
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>
                    <div className='flex gap-2 items-center'>
                      <input
                        type='checkbox'
                        id='alertsEnabled'
                        checked={formData.alertsEnabled}
                        onChange={(e) => handleInputChange('alertsEnabled', e.target.checked)}
                      />
                      <label htmlFor='alertsEnabled'>Get analyst alerts for this watchlist</label>
                    </div>
                  </div>

                  {/* Screening Criteria - Only for Dynamic */}
                  {formData.type === 'dynamic' && (
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                         <Search size={18} className="text-gray-900" />
                         <h3 className="text-lg font-semibold text-gray-900">
                           Screening Criteria
                         </h3>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <div className="flex items-center justify-between mb-3">
                           <span className="text-sm font-semibold text-blue-800">Add Screening Condition</span>
                        </div>
                        
                        {formData.criteria.map((condition, index) => {
                          const selectedMetric = availableMetrics.find(m => m.category === condition.key);
                          const isDate = selectedMetric?.datatype === 'Char' && selectedMetric?.category.toLowerCase().includes('date');
                          const meta = metricMeta[condition.key];
                          
                          // A metric is numeric if it's explicitly Decimal/Percentage, 
                          // OR if it's a Char field (non-date) that has a help_range but no options data.
                          const isNumeric = (selectedMetric?.datatype === 'Decimal' || selectedMetric?.datatype === 'Percentage') ||
                                           (selectedMetric?.datatype === 'Char' && !isDate && meta?.help_range && (!meta?.data || meta.data.length === 0));
                          
                          const isCategorical = selectedMetric?.datatype === 'Char' && !isDate && !isNumeric;

                          return (
                            <div key={index} className="grid grid-cols-12 gap-3 items-end mb-3 last:mb-0">
                              {/* Metric */}
                              <div className={condition.key ? "col-span-12 sm:col-span-4" : "col-span-11"}>
                                <select
                                  value={condition.key}
                                  onChange={(e) => handleConditionChange(index, 'key', e.target.value)}
                                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                  <option value="">Select field</option>
                                  {availableMetrics.map((m) => (
                                    <option key={m.category} value={m.category}>
                                      {formatMetricLabel(m.category)}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {condition.key && (
                                <>
                                  {/* Operator - Only for Numeric */}
                                  {isNumeric && (
                                    <div className="col-span-12 sm:col-span-2">
                                      <select
                                        value={condition.op}
                                        onChange={(e) => handleConditionChange(index, 'op', e.target.value)}
                                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                      >
                                        <option value="">Op</option>
                                        {operators.map((op) => (
                                          <option key={op.value} value={op.value}>
                                            {op.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {/* Value - Responsive width based on Operator visibility */}
                                  <div className={`col-span-12 ${isNumeric ? 'sm:col-span-4' : 'sm:col-span-6'}`}>
                                    {isDate ? (
                                    <div className="space-y-1">
                                        {meta?.help_range && (
                                          <p className="text-[10px] text-gray-500 ml-1">
                                            Range: {meta.help_range.min} to {meta.help_range.max}
                                          </p>
                                        )}
                                        <div className="flex gap-2">
                                          <input
                                            type="date"
                                            value={condition.value.split(',')[0] || ''}
                                            onChange={(e) => {
                                              const dates = condition.value.split(',');
                                              handleConditionChange(index, 'value', `${e.target.value},${dates[1] || ''}`);
                                            }}
                                            className="w-1/2 px-2 py-2 bg-white border border-gray-300 rounded-lg text-xs"
                                          />
                                          <input
                                            type="date"
                                            value={condition.value.split(',')[1] || ''}
                                            onChange={(e) => {
                                              const dates = condition.value.split(',');
                                              handleConditionChange(index, 'value', `${dates[0] || ''},${e.target.value}`);
                                            }}
                                            className="w-1/2 px-2 py-2 bg-white border border-gray-300 rounded-lg text-xs"
                                          />
                                        </div>
                                      </div>
                                    ) : isCategorical ? (
                                      <SimpleMultiSelect
                                        value={condition.value ? condition.value.split(',') : []}
                                        options={meta?.data || []}
                                        onChange={(values) => handleConditionChange(index, 'value', values.join(','))}
                                        placeholder="Select options"
                                      />
                                    ) : (
                                      <div className="space-y-1">
                                        {isNumeric && meta?.help_range && (
                                          <p className="text-[10px] text-gray-500 ml-1">
                                            Range: {meta.help_range.min} to {meta.help_range.max}
                                          </p>
                                        )}
                                        <input
                                          type="text"
                                          value={condition.value}
                                          onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                                          placeholder="Value"
                                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}

                              {/* Remove */}
                              <div className={condition.key ? "col-span-12 sm:col-span-2 flex justify-end" : "col-span-1 flex justify-end"}>
                                {formData.criteria.length > 1 && (
                                  <button
                                    onClick={() => removeCondition(index)}
                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <X size={16} />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        <button
                          onClick={addCondition}
                          className="w-full py-2.5 mt-2 bg-blue-400 bg-opacity-30 text-blue-700 rounded-lg hover:bg-opacity-40 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                        >
                          + Add Condition
                        </button>
                      </div>
                    </div>
                  )}



                  {/* Static content specific with MultiSelect */}
                  {formData.type === 'static' && (
                     <div className="pt-4 border-t border-gray-100 space-y-4">
                       <div className="flex items-center gap-2">
                         <Pin size={18} className="text-gray-900" />
                         <h3 className="text-lg font-semibold text-gray-900">
                           Select Stocks
                         </h3>
                       </div>
                       <MultiSelect
                         value={formData.staticStocks}
                         onChange={(values) => handleInputChange('staticStocks', values)}
                         placeholder="Search symbols to add..."
                       />
                       <p className="text-xs text-gray-500 italic">
                         Select stocks to follow manually.
                       </p>
                     </div>
                  )}

                </div>
              )}
            </div>

            {/* Show Modal Error Here */}
            {modalError && (
              <div className="px-6 py-2 bg-red-50 border-l-4 border-red-500 mx-6 mb-4">
                <p className="text-red-700 text-sm font-medium">{modalError}</p>
              </div>
            )}

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 bg-opacity-50">
               {step === 1 ? (
                 <>
                   <button
                     onClick={() => setIsModalOpen(false)}
                     className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                   >
                     Cancel
                   </button>
                   <button
                     onClick={() => setStep(2)}
                     disabled={!formData.type}
                     className={`px-6 py-2 rounded-lg transition-colors text-sm font-medium ${
                       formData.type 
                         ? 'bg-blue-600 text-white hover:bg-blue-700' 
                         : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                     }`}
                   >
                     Create Watchlist
                   </button>
                 </>
               ) : (
                 <>
                   <button
                     onClick={() => setStep(1)}
                     className="flex items-center gap-2 px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                   >
                     <ArrowLeft size={16} />
                     Back
                   </button>
                   <div className="flex gap-3">
                     <button
                        onClick={() => setIsModalOpen(false)}
                        className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                     >
                       Cancel
                     </button>
                     <button
                       onClick={handleCreateWatchlist}
                       disabled={createLoading}
                       className={`px-6 py-2 rounded-lg transition-colors text-sm font-medium ${createLoading ? 'bg-gray-300 text-gray-700 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                     >
                       {createLoading ? 'Creating...' : 'Create Watchlist'}
                     </button>
                   </div>
                 </>
               )}
            </div>
          </div>
        </div>
      )}

      {/* Watchlist Detail Modal */}
      <WatchlistDetailModal
        watchlistId={selectedWatchlistId}
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        onUpdateSuccessful={fetchWatchlists}
        onDeleteSuccessful={fetchWatchlists}
      />
    </div>
  );
};

export default WatchlistPage;

